import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag, SocketEventTypes } from '@app/enums';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffType } from '@app/models/time-off-v3/time-off-type.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TableOptions } from '@app/modules/table/interfaces/table-options.interface';
import { TimeOffService } from '@app/modules/time-off-v3/services';
import { TimeOffModuleMenuService } from '@app/modules/time-off-v3/services/time-off-module-menu.service';
import { AnalyticService, AuthService, NotifyService, SocketService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { PolicyReassignForm } from '@time-off-v3/forms';
import { AssignmentEventPayload } from '@time-off-v3/interfaces/assignment-event-payload.interface';
import { TypeAndPolicyTable } from '@time-off-v3/services/type-and-policy-table/type-and-policy.table';
import { Subscription } from 'rxjs';

interface TimeOffTypeAndPolicies {
    timeOffType: TimeOffType;
    timeOffPolicies: TimeOffPolicy[];
}

@Component({
    templateUrl: './types-and-policy-index.template.html',
    styleUrls: ['./types-and-policy-index.style.scss'],
})
export class TypesAndPolicyIndexView implements OnInit, OnDestroy {
    @ViewChild('deletePolicyModal') deletePolicyModal: DeprecatedConfirmationDialogComponent;
    @ViewChild('deleteTypeModal') deleteTypeModal: DeprecatedConfirmationDialogComponent;
    @ViewChild('policyReassignDialog') policyReassignDialog: DeprecatedModalFormComponent;
    @ViewChild('policyReassignForm') policyReassignForm: PolicyReassignForm;

    hasTimeOffImprovement = false;
    timeOffTypeAndPolicies: TimeOffTypeAndPolicies[] = [];
    timeOffTypes: TimeOffType[] = [];
    typeAndPolicyTables: TypeAndPolicyTable[] = [];
    table: TypeAndPolicyTable;
    isLoading = false;
    assignmentsPayloads: AssignmentEventPayload[] = [];

    private socketSubscription: Subscription;

    constructor(
        private auth: AuthService,
        private analyticService: AnalyticService,
        private featureService: FeatureService,
        private notify: NotifyService,
        private router: Router,
        private route: ActivatedRoute,
        private timeOffService: TimeOffService,
        private socketService: SocketService,
        public timeOffModuleMenuService: TimeOffModuleMenuService
    ) {}

    async ngOnInit(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffTypesPolicies);

        this.socketSubscription = this.socketService
            .getCompanyChannel(SocketEventTypes.policyAssignmentProgressed)
            .subscribe((payload: AssignmentEventPayload) => {
                this.setEventPayload(payload);

                if (payload.total === payload.assigned + payload.unassigned) {
                    this.socketSubscription.unsubscribe();
                    this.loadData();
                }
            });

        this.hasTimeOffImprovement = await this.featureService.has(FeatureFlag.timeOffImprovements);
        this.loadData();
    }

    setEventPayload(receivedPayload: AssignmentEventPayload): void {
        const index = this.assignmentsPayloads.findIndex(
            (payload) => payload.time_off_type_id === receivedPayload.time_off_type_id
        );

        if (index === -1) {
            this.assignmentsPayloads.push(receivedPayload);
            return;
        }

        this.assignmentsPayloads[index] = receivedPayload;
    }

    getEventPayload(timeOffTypeId: number): AssignmentEventPayload {
        const payload = this.assignmentsPayloads.find((payload) => payload.time_off_type_id === timeOffTypeId);

        return payload || { total: 1, assigned: 0, unassigned: 0, time_off_type_id: timeOffTypeId };
    }

    async loadData(): Promise<void> {
        this.typeAndPolicyTables = [];

        this.isLoading = true;

        try {
            const [timeOffTypes] = await TimeOffType.with('timeOffPolicies').all();
            this.timeOffTypes = timeOffTypes;
            this.typeAndPolicyTables = this.timeOffTypes.map(
                (timeOffType: TimeOffType) =>
                    new TypeAndPolicyTable(this.auth, timeOffType, this.hasTimeOffImprovement, {
                        canReassign: timeOffType.timeOffPolicies.length > 1,
                    })
            );
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }

        this.isLoading = false;
    }

    createNewPolicy(timeOffTypeId: number): void {
        this.router.navigate(['policy', 'create'], { relativeTo: this.route, queryParams: { timeOffTypeId } });
    }

    async onDeleteTimeOffType(timeOffType: TimeOffType): Promise<void> {
        try {
            const approved = await this.deleteTypeModal.show();

            if (!approved) {
                return;
            }
            this.isLoading = true;
            await timeOffType.delete();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyDeleteTimeOffType);
            this.notify.success('time-off-v3.timeOffTypeHasBeenDeleted');
            this.loadData();
            this.isLoading = false;
        } catch (err) {
            this.notify.error(err);
            this.isLoading = false;
        }
    }

    async onMarkAsDefaultTimeOffType(timeOffType: TimeOffType): Promise<void> {
        this.isLoading = true;

        try {
            timeOffType.isDefault = true;
            await timeOffType.save();
            this.notify.success('time-off-v3.timeOffTypeHasBeenMarkedAsDefault');
            this.loadData();
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async onCopyTimeOffPolicy(timeOffPolicyId: number): Promise<void> {
        this.isLoading = true;

        try {
            await this.timeOffService.createCopy(timeOffPolicyId);
            this.notify.success('time-off-v3.timeOffPolicyHasBeenCopied');
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async onDeletePolicy(timeOffPolicy: TimeOffPolicy): Promise<void> {
        try {
            const approved = await this.deletePolicyModal.show();

            if (!approved) {
                return;
            }

            this.isLoading = true;
            await timeOffPolicy.delete();

            this.analyticService.trackEvent(AnalyticEvents.TimeOffTimeOffPolicyDeleteTimeOffPolicy);

            this.notify.success('time-off-v3.timeOffPolicyHasBeenDeleted');
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async menuAction(event: MenuColumnEvent, table: TypeAndPolicyTable): Promise<void> {
        const { menuItem, data } = event;
        const timeOffPolicy = data as TimeOffPolicy;

        switch (menuItem.event) {
            case TypeAndPolicyTable.edit.event:
                this.hasTimeOffImprovement
                    ? this.router.navigate(['../policy', timeOffPolicy.id, 'details'], { relativeTo: this.route })
                    : this.router.navigate(['policies', timeOffPolicy.id, 'edit'], { relativeTo: this.route });
                break;
            case TypeAndPolicyTable.assign.event:
                this.router.navigate(['policy', 'assign', timeOffPolicy.id], { relativeTo: this.route });
                break;
            case TypeAndPolicyTable.reassign.event: {
                const targetType = this.timeOffTypes.find(
                    (timeOffType) => timeOffType.id === timeOffPolicy.timeOffTypeId
                );
                this.policyReassignForm.sourcePolicy = timeOffPolicy;
                this.policyReassignForm.policies = targetType.timeOffPolicies.filter((p) => p.id !== timeOffPolicy.id);
                this.policyReassignDialog.show();
                break;
            }
            case TypeAndPolicyTable.copy.event:
                await this.onCopyTimeOffPolicy(timeOffPolicy.id);
                table.reload();
                break;
            case TypeAndPolicyTable.delete.event:
                await this.onDeletePolicy(timeOffPolicy);
                table.reload();
        }
    }

    getTimeOffTypeTableOptions(timeOffType: TimeOffType): TableOptions {
        return {
            badgeType: timeOffType.isDefault ? 'default' : null,
            badgeLabel: timeOffType.isDefault ? 'time-off-v3.types-and-policies-table.defaultPillLabel' : null,
            iconName: timeOffType.icon,
            iconColor: timeOffType.color,
        };
    }

    ngOnDestroy(): void {
        this.socketSubscription.unsubscribe();
    }

    async onReassignPolicy(): Promise<void> {
        await this.timeOffService.reAssignEmployeesToPolicy(
            this.policyReassignForm.sourcePolicy.id,
            this.policyReassignForm.selectPolicy,
            {
                fromHireDate: !this.policyReassignForm.isCustomDate,
                effectiveAt: this.policyReassignForm.effectiveAt,
            }
        );
        this.notify.success('time-off-v3.employeesSuccessfullyAssignedToPolicy');
        this.loadData();
    }
}
